import { useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const PRIORITY_ICON = {
  high: <AlertCircle className="h-5 w-5 text-red-600" />,
  medium: <AlertTriangle className="h-5 w-5 text-yellow-600" />,
  low: <AlertCircle className="h-5 w-5 text-blue-600" />,
};

// Global, headless listener for printer alerts. Renders no DOM — it
// subscribes to active+unacknowledged alerts and pops a toast per new one.
// Superadmin only, since that's the role responsible for printer ops.
const PrinterAlertToasts = () => {
  const { userRole } = useAuth();
  // First snapshot is the existing backlog; don't blast the user with a
  // wall of toasts on page load. After the initial read, only new docs toast.
  const initialized = useRef(false);
  const seen = useRef(new Set());

  useEffect(() => {
    if (userRole !== 'superadmin') return;

    initialized.current = false;
    seen.current = new Set();

    const alertsQuery = query(
      collection(db, 'printer_alerts'),
      where('status', '==', 'active'),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(alertsQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type !== 'added') return;
        const data = change.doc.data();
        const id = change.doc.id;
        if (seen.current.has(id)) return;
        seen.current.add(id);
        if (!initialized.current) return;
        if (data.acknowledged) return;

        toast.custom((t) => (
          <div
            className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto flex border border-gray-200`}
          >
            <div className="flex-1 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">{PRIORITY_ICON[data.priority] || PRIORITY_ICON.low}</div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">{data.booth_name || 'Printer alert'}</p>
                  <p className="mt-1 text-sm text-gray-600">{data.message}</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="border-l border-gray-200 px-3 text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        ), { duration: 8000 });
      });

      initialized.current = true;
    });

    return () => unsubscribe();
  }, [userRole]);

  return null;
};

export default PrinterAlertToasts;
